import {
  IQuickVideoPartForm,
  IQuickVideoPartPositionOption,
  QuickVideoPartType,
} from 'lib/api/quickVideo/types';
import * as Yup from 'yup';

export const quickVideoValidationSchema = ({
  requiredPositions,
}: {
  requiredPositions: IQuickVideoPartPositionOption[];
}) => {
  return Yup.object({
    name: Yup.string()
      .required('Title is required')
      .max(60, 'Title must be at most 60 characters')
      .test(
        'not-just-spaces',
        'Please enter a valid title',
        value => !value || value.trim().length > 0
      ),
    description: Yup.string()
      .max(60, 'Description must be at most 60 characters')
      .test(
        'not-just-spaces',
        'Please enter a valid description',
        value => !value || value.trim().length > 0
      ),

    quickVideoParts: Yup.array()
      .of(
        Yup.object().shape({
          partPosition: Yup.string(),
          description: Yup.string().max(
            250,
            'Description must be at most 250 characters'
          ),
          partType: Yup.string(),
          video: Yup.object().nullable(),
        })
      )
      .test(
        'all-required-positions-selected',
        'Please fill out all required video positions.',
        quickVideoParts => {
          const areRequiredPartsValid = requiredPositions.every(reqPos => {
            const found = (quickVideoParts || []).find(
              (part: IQuickVideoPartForm) => {
                return part.partPosition === reqPos.value && !!part.partType; // must have partType
              }
            );
            if (!found) return false;
            if (
              found.partType === QuickVideoPartType.DEALERSHIP_VIDEO &&
              !found.video
            ) {
              return false;
            }
            return true;
          });

          const areDealershipPartsValid = quickVideoParts
            .filter(
              (part: IQuickVideoPartForm) =>
                part.partType === QuickVideoPartType.DEALERSHIP_VIDEO
            )
            .every((part: IQuickVideoPartForm) => !!part.video);

          const isDescriptionValid = quickVideoParts.every(
            (part: IQuickVideoPartForm) =>
              (part.description || '').length <= 150
          );

          return (
            areRequiredPartsValid &&
            areDealershipPartsValid &&
            isDescriptionValid
          );
        }
      ),

    smsText: Yup.string()
      .max(160, 'SMS can be at most 160 characters')
      .test(
        'not-just-spaces',
        'SMS text is required',
        value => !value || value.trim().length > 0
      )
      .when(['generateEmailSmsContent'], {
        is: 0,
        then: Yup.string().required('SMS text is required'),
      }),
    emailContent: Yup.object()
      .shape({
        subject: Yup.string(),
        body: Yup.string(),
        elements: Yup.array(),
        signature: Yup.object(),
      })
      .when(['generateEmailSmsContent'], {
        is: 0,
        then: Yup.object().shape({
          subject: Yup.string()
            .max(160, 'Email subject can be at most 160 characters')
            .required('Email subject is required')
            .test(
              'not-just-spaces',
              'Please enter a valid subject',
              value => !value || value.trim().length > 0
            ),
          body: Yup.string()
            .test(
              'not-just-html-tags',
              'Please enter a valid email body',
              value =>
                typeof value === 'string' &&
                value.replaceAll('<p>', '').replaceAll('</p>\n', '').trim()
                  .length > 0
            )
            .required('Email body is required'),
          elements: Yup.array(),
          signature: Yup.object(),
        }),
      }),
  });
};
